import { ErrorCode } from '@/errors/errorCode'
import { logError } from '@/errors/log-error'
import {
  getMarketingConsent,
  subscribeMarketingConsent,
  type MarketingConsentState,
} from '@/utils/ads/marketing-consent'
import type { AdDeviceType, AnchorFormat } from '@/utils/ads/types'

export type AdSlotRegistration = {
  adUnit: string
  size: [number, number]
  divId: string
  deviceType: AdDeviceType
  /**
   * The element the IntersectionObserver watches. Must be a laid-out element,
   * not the GPT div, which is display:none until the ad renders.
   */
  element: HTMLElement
  targetKey?: string
  targetValue?: string
  onRenderEnded?: (isEmpty: boolean) => void
}

export type AnchorSlotRegistration = {
  adUnit: string
  format: AnchorFormat
  targetKey?: string
  targetValue?: string
  onDisplayed?: () => void
  onRenderEnded?: (isEmpty: boolean) => void
}

type Registration =
  | ({ kind: 'in-page' } & AdSlotRegistration)
  | ({ kind: 'anchor' } & AnchorSlotRegistration)

const ROOT_MARGIN: Record<AdDeviceType, string> = {
  mobile: '200% 0%',
  tablet: '200% 0%',
  desktop: '100% 0%',
}

const pending = new Map<string, Registration>()
const defined = new Map<
  string,
  { slot: googletag.Slot; registration: Registration }
>()
const observedKeys = new WeakMap<Element, string>()
const slotKeys = new Map<googletag.Slot, string>()

let observer: IntersectionObserver | undefined
let consent: MarketingConsentState = 'unknown'
let consentSubscribed = false
let gptReady = false
let flushScheduled = false
let renderListenerAttached = false

const keyOf = (registration: Registration) =>
  registration.kind === 'in-page'
    ? registration.divId
    : `anchor:${registration.adUnit}`

const attachRenderListener = () => {
  if (renderListenerAttached) return
  renderListenerAttached = true

  // One listener for the whole page. The previous implementation attached a
  // fresh global listener inside every defineSlot call, leaking one listener
  // per ad per client-side navigation.
  window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
    const key = slotKeys.get(event.slot)
    const record = key ? defined.get(key) : undefined

    record?.registration.onRenderEnded?.(event.isEmpty)
  })
}

const refreshSlots = (slots: googletag.Slot[]) => {
  if (!slots.length) return

  window.googletag.cmd.push(() => {
    try {
      window.googletag.pubads().refresh(slots)
    } catch (error) {
      logError(ErrorCode.e00281, error)
    }
  })
}

const onIntersect: IntersectionObserverCallback = (entries, io) => {
  const slots: googletag.Slot[] = []

  entries.forEach((entry) => {
    if (!entry.isIntersecting) return

    const key = observedKeys.get(entry.target)
    const record = key ? defined.get(key) : undefined
    if (!record) return

    io.unobserve(entry.target)
    observedKeys.delete(entry.target)
    slots.push(record.slot)
  })

  refreshSlots(slots)
}

const getObserver = (deviceType: AdDeviceType) => {
  // Every registration carries the same device type, because they all read the
  // one server-computed value, so the first one decides the margin.
  if (!observer) {
    observer = new IntersectionObserver(onIntersect, {
      rootMargin: ROOT_MARGIN[deviceType],
    })
  }

  return observer
}

const flush = () => {
  if (consent !== 'granted' || !gptReady || !pending.size) return

  const registrations = [...pending.values()]
  pending.clear()

  try {
    window.googletag.cmd.push(() => {
      const anchorSlots: googletag.Slot[] = []

      registrations.forEach((registration) => {
        try {
          const slot =
            registration.kind === 'in-page'
              ? window.googletag.defineSlot(
                  registration.adUnit,
                  registration.size,
                  registration.divId
                )
              : window.googletag.defineOutOfPageSlot(
                  registration.adUnit,
                  window.googletag.enums.OutOfPageFormat[registration.format]
                )

          if (!slot) {
            logError(
              ErrorCode.e00281,
              new Error(
                `GPT declined to define a slot for ad unit ${registration.adUnit}`
              )
            )
            return
          }

          slot.addService(window.googletag.pubads())

          if (registration.targetKey && registration.targetValue) {
            slot.setTargeting(registration.targetKey, registration.targetValue)
          }

          defined.set(keyOf(registration), { slot, registration })
          slotKeys.set(slot, keyOf(registration))

          if (registration.kind === 'in-page') {
            // disableInitialLoad means display() registers the container
            // without fetching; the observer decides when to fetch.
            window.googletag.display(registration.divId)
            observedKeys.set(registration.element, registration.divId)
            getObserver(registration.deviceType).observe(registration.element)
            return
          }

          // Out-of-page slots have no container to observe, so they are
          // fetched as soon as they are defined.
          window.googletag.display(slot)
          anchorSlots.push(slot)
          registration.onDisplayed?.()
        } catch (error) {
          logError(ErrorCode.e00281, error)
        }
      })

      refreshSlots(anchorSlots)
    })
  } catch (error) {
    logError(ErrorCode.e00281, error)
    registrations.forEach((registration) => {
      pending.set(keyOf(registration), registration)
    })
  }
}

const scheduleFlush = () => {
  if (flushScheduled) return
  if (consent !== 'granted' || !gptReady || !pending.size) return

  flushScheduled = true

  // The frame callback runs after React has committed, so every ad component
  // that mounted in the same commit lands in one flush — one batched request
  // instead of one per component.
  window.requestAnimationFrame(() => {
    flushScheduled = false
    flush()
  })
}

const ensureConsent = () => {
  if (consentSubscribed) return
  consentSubscribed = true

  consent = getMarketingConsent()

  subscribeMarketingConsent((state) => {
    consent = state

    if (state === 'denied') {
      pending.clear()
      destroyAll()
    }

    scheduleFlush()
  })
}

const ensureGpt = () => {
  window.googletag = window.googletag || { cmd: [] }
  if (gptReady) return

  window.googletag.cmd.push(() => {
    gptReady = true
    attachRenderListener()
    scheduleFlush()
  })
}

const destroyAll = () => {
  const slots = [...defined.values()].map((record) => record.slot)

  defined.forEach((record) => {
    if (record.registration.kind === 'in-page') {
      observer?.unobserve(record.registration.element)
      observedKeys.delete(record.registration.element)
    }

    slotKeys.delete(record.slot)
  })

  defined.clear()

  if (!slots.length) return

  try {
    window.googletag.destroySlots(slots)
  } catch (error) {
    logError(ErrorCode.e00281, error)
  }
}

const unregister = (key: string) => {
  pending.delete(key)

  const record = defined.get(key)
  if (!record) return

  defined.delete(key)
  slotKeys.delete(record.slot)

  if (record.registration.kind === 'in-page') {
    observer?.unobserve(record.registration.element)
    observedKeys.delete(record.registration.element)
  }

  try {
    window.googletag.destroySlots([record.slot])
  } catch (error) {
    logError(ErrorCode.e00281, error)
  }
}

const register = (registration: Registration): (() => void) => {
  if (typeof window === 'undefined') return () => {}

  const key = keyOf(registration)
  pending.set(key, registration)

  try {
    ensureConsent()
    ensureGpt()
    scheduleFlush()
  } catch (error) {
    logError(ErrorCode.e00281, error)
  }

  return () => unregister(key)
}

export const registerAdSlot = (registration: AdSlotRegistration) =>
  register({ kind: 'in-page', ...registration })

export const registerAnchorAdSlot = (registration: AnchorSlotRegistration) =>
  register({ kind: 'anchor', ...registration })
